import React, { useState } from 'react';
import { Box, ContentTemplate, HeaderTemplate, Progress, Stepper, TitleHeader } from 'web-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { makeStyles } from 'tss-react/mui';
import { useGetRequestSummary } from '../../../api-refactored/group-special-consideration/useGetRequestSummary';
import DownloadTemplate from './DownloadTemplate';
import { FileProcessingState, FileUploadState } from '../../../models/File';
import UploadSection from './UploadSection';
import ReasonSection from './ReasonSection';
import DraftTimeStamp from '../../../components/DraftTimeStamp';

interface UploadRankingDetailProps {
  routeSteps: { label: string }[];
  amendment: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-10%',
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-8%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-2%',
    },
  },
  reasonContainer: {
    marginBottom: theme.spacing(7),
  },
}));

const UploadRankingDetail = ({ routeSteps, amendment }: UploadRankingDetailProps) => {
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const { classes } = useStyles();

  const [uploadAndProcessState, setUploadAndProcessState] = useState<
    FileUploadState | FileProcessingState
  >(FileUploadState.IDLE);
  const { data: applicationData, isLoading } = useGetRequestSummary({
    uuid: applicationUuid,
    refetchInterval: uploadAndProcessState === FileProcessingState.PROCESSING ? 5000 : undefined,
  });

  return (
    <>
      <TitleHeader
        title={t(
          'specialConsideration->>ApplyForSpecialConsideration.ApplyForSpecialConsideration'
        )}
      />
      <HeaderTemplate>
        <Stepper className={classes.stepper} activeStep={0} steps={routeSteps} />
      </HeaderTemplate>
      {isLoading && (
        <ContentTemplate transparent>
          <Progress color="secondary" />
        </ContentTemplate>
      )}
      {applicationData && (
        <>
          <ContentTemplate isContinuable>
            <DownloadTemplate groupScType={applicationData.groupScType} amendment={amendment} />
          </ContentTemplate>
          <ContentTemplate continuous isDividerTop>
            <UploadSection
              uploadAndProcessState={uploadAndProcessState}
              setUploadAndProcessState={setUploadAndProcessState}
              amendment={amendment}
            />
          </ContentTemplate>
          <div style={{ zIndex: 0 }}>
            <ContentTemplate type="large" continuous isDividerTop>
              <Box className={classes.reasonContainer}>
                <ReasonSection
                  disabled={uploadAndProcessState !== FileProcessingState.SUCCESS}
                  reasonData={applicationData}
                  amendment={amendment}
                />
                <DraftTimeStamp savedAt={applicationData.lastUpdatedTime} />
              </Box>
            </ContentTemplate>
          </div>
        </>
      )}
    </>
  );
};

export default UploadRankingDetail;
